import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDown, ArrowRight, Download, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LazyImage, StatusPill } from '@/components/ui/Primitives';
import { SocialIcon } from '@/components/ui/SocialIcon';
import { localized, type Profile, type SocialLink } from '@/lib/types';
import { useI18n } from '@/store/preferences';

interface HeroProps {
  profile: Profile;
  socialLinks: SocialLink[];
  projectCount: number;
}

export function Hero({ profile, socialLinks, projectCount }: HeroProps) {
  const { t, locale } = useI18n();
  const reduceMotion = useReducedMotion();

  const availabilityLabel =
    profile.availability === 'OPEN_TO_WORK'
      ? t.hero.available
      : profile.availability === 'FREELANCE'
        ? t.hero.freelance
        : t.hero.notLooking;

  const resumeUrl = locale === 'es' ? profile.resumeEsUrl : profile.resumeEnUrl;
  const yearsCoding = new Date().getFullYear() - profile.startedCodingYear;

  const rise = (delay: number) => ({
    initial: { opacity: 0, y: reduceMotion ? 0 : 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] as const },
  });

  return (
    <section className="relative overflow-hidden pt-32 sm:pt-40">
      {/* Faint grid that fades out downward — gives the empty space structure. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px] grid-lines opacity-[0.35]"
        style={{ maskImage: 'linear-gradient(to bottom, black, transparent)' }}
        aria-hidden
      />

      <div className="shell relative">
        {/* An explicit track, not `auto`: with `auto` the column collapses to
            the photo's width, so `mx-auto` on the image has nothing to centre
            within and it ends up flush against the right edge. */}
        <div className="grid gap-14 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-center lg:gap-16">
          <div>
            <motion.div {...rise(0)}>
              <StatusPill
                label={availabilityLabel}
                live={profile.availability !== 'NOT_LOOKING'}
              />
            </motion.div>

            <motion.h1 {...rise(0.08)} className="display mt-7 text-balance">
              {profile.fullName}
            </motion.h1>

            <motion.p
              {...rise(0.16)}
              className="mt-5 text-[clamp(1.15rem,2.4vw,1.6rem)] font-medium tracking-tight text-accent"
            >
              {localized(profile, 'headline', locale)}
            </motion.p>

            <motion.p {...rise(0.24)} className="lede mt-6 max-w-xl text-pretty">
              {localized(profile, 'summary', locale)}
            </motion.p>

            <motion.div {...rise(0.32)} className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/projects" className="btn-primary group">
                {t.hero.viewProjects}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>

              {resumeUrl && (
                <a href={resumeUrl} download className="btn-outline">
                  <Download className="h-4 w-4" />
                  {t.hero.downloadCv}
                </a>
              )}

              <Link to="/contact" className="btn-ghost">
                {t.hero.getInTouch}
              </Link>
            </motion.div>

            <motion.div
              {...rise(0.4)}
              className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4"
            >
              <Stat value={`${projectCount}`} label={t.common.projects} />
              <Stat value={`${yearsCoding}+`} label={locale === 'es' ? 'años programando' : 'years coding'} />

              <div className="flex items-center gap-2 text-sm text-muted">
                <MapPin className="h-3.5 w-3.5 text-faint" aria-hidden />
                {localized(profile, 'location', locale)}
              </div>

              <ul className="flex items-center gap-1.5">
                {socialLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.url}
                      target={link.url.startsWith('mailto:') ? undefined : '_blank'}
                      rel="noopener noreferrer"
                      className="btn-icon"
                      aria-label={link.label}
                    >
                      <SocialIcon iconKey={link.iconKey} className="h-4 w-4" />
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {profile.photoUrl && (
            <motion.div
              initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto w-full max-w-[280px] lg:max-w-none"
            >
              <div className="absolute -inset-3 -z-10 rounded-[2rem] bg-accent/10 blur-2xl" aria-hidden />
              <LazyImage
                src={profile.photoUrl}
                alt={profile.fullName}
                aspect="aspect-[4/5]"
                priority
                className="rounded-3xl border border-line"
              />
            </motion.div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="mt-20 flex justify-center sm:mt-28"
          aria-hidden
        >
          <div className="flex flex-col items-center gap-2 text-faint">
            <span className="font-mono text-2xs uppercase tracking-[0.24em]">{t.hero.scroll}</span>
            <motion.span
              animate={reduceMotion ? {} : { y: [0, 6, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <ArrowDown className="h-4 w-4" />
            </motion.span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-semibold tracking-tight">{value}</span>
      <span className="text-sm text-muted">{label}</span>
    </div>
  );
}
